import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { CameraIcon, CrossCircledIcon, PlusIcon } from "@radix-ui/react-icons";
import _ from "lodash";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import resizeImageFromDataUrl from "src/lib/resizeImage";
import { addPhoto } from "src/lib/slices/alcoholSlice";
import { v4 as uuidv4 } from "uuid";
import Image from "../Image";
import PhotoPreview from "../PhotoPreview";
import { Button, ButtonStack } from "../ui/button";
import AddInPastButton from "../addInPastButton";

export default function PhotoSelector() {
  const dispatch = useDispatch();

  const photoRef = useRef<HTMLInputElement>(null);
  const [photoData, setPhotoData] = useState<string | null>(null);

  const locationState = useSelector((state: any) => state.location);

  const getLatLong = () => {
    const lastLocationUpdate = locationState.lastUpdate;

    if (new Date().getTime() - lastLocationUpdate < 10 * 60 * 1000) {
      return {
        latitude: locationState.latitude,
        longitude: locationState.longitude,
      };
    }

    return {};
  };

  const add = ({ date }: { date?: number } = {}) => {
    const { latitude, longitude } = getLatLong();
    dispatch(
      addPhoto({
        id: uuidv4(),
        date: date ?? new Date().getTime(),
        photoData,
        latitude,
        longitude,
      })
    );

    setPhotoData(null);

    toast("Fotka byla přidána");

    if (Capacitor.isNativePlatform())
      (async () => {
        Haptics.impact({ style: ImpactStyle.Light });
        _.delay(() => Haptics.impact({ style: ImpactStyle.Light }), 250);
      })();
  };

  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);

  return (
    <>
      <div className="h-32">
        {photoData && (
          <Image
            style={{
              height: "100%",
              maxWidth: "100%",
              objectFit: "cover",
              cursor: "pointer",
              borderRadius: "0.5rem",
              margin: "0 auto",
            }}
            src={photoData}
            alt="Fotka uživatele"
            onClick={() => setPhotoPreviewOpen(true)}
          />
        )}
      </div>
      <div
        style={{
          paddingTop: "0.5rem",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          gap: "0.5rem",
          position: "relative",
        }}
      >
        <input
          type="file"
          accept="image/png, image/jpeg"
          name="photo"
          id="photo"
          ref={photoRef}
          style={{ display: "none" }}
          onChange={(event) => {
            event.preventDefault();
            const { files } = event.target;
            if (!files?.[0]) return;
            const reader = new FileReader();
            reader.onloadend = async () => {
              const dataUrl = await resizeImageFromDataUrl({
                dataUrl: reader.result as string,
              });
              if (!dataUrl) return;
              setPhotoData(dataUrl);
            };
            reader.readAsDataURL(files[0]);
          }}
        />
        {photoData && (
          <Button
            size="icon"
            variant="destructive"
            className="absolute right-4"
            onClick={() => {
              setPhotoData(null);
            }}
          >
            <CameraIcon />
            <CrossCircledIcon />
          </Button>
        )}
        {!photoData && (
          <Button
            size="icon"
            className="absolute right-4"
            onClick={() => {
              if (Capacitor.isNativePlatform()) {
                Camera.getPhoto({
                  quality: 100,
                  allowEditing: false,
                  resultType: CameraResultType.DataUrl,
                  source: CameraSource.Camera,
                })
                  .then(async (photo) => {
                    const dataUrl = await resizeImageFromDataUrl({
                      dataUrl: photo.dataUrl as string,
                    });
                    setPhotoData(dataUrl);
                  })
                  .catch(() => {});
              } else {
                photoRef.current?.click();
              }
            }}
          >
            <CameraIcon />
            <PlusIcon />
          </Button>
        )}

        <ButtonStack disabled={!photoData}>
          <AddInPastButton
            actionLabel="Přidat"
            onSubmit={add}
            disabled={!photoData}
          />
          <Button onClick={() => add()} stack="right" disabled={!photoData}>
            Přidat
          </Button>
        </ButtonStack>
      </div>
      <PhotoPreview
        setOpen={setPhotoPreviewOpen}
        open={photoPreviewOpen}
        imgUrl={photoData}
        alt="Fotka uživatele"
      />
    </>
  );
}
